import {
  IExhibitorPair,
  INotificationBaseRelatedToShow,
  IPayloadUpdatedExhibitorPair,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RequestKey } from "api/request-key";
import { apiV2 } from "api/firebase";
import { useEffect } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { SHOW_EXHIBITORS_PATH } from "view/routes";

type UseGlossaryArgs = {
  showId: string;
  exhibitorPairId?: string;
};

export type UseExpoPlatformRes = [IExhibitorPair | null, boolean];

function useExhibitorPair({
  showId,
  exhibitorPairId,
}: UseGlossaryArgs): UseExpoPlatformRes {
  const queryClient = useQueryClient();
  const history = useNavigate();

  const { data: exhibitorPair, isFetching: isLoading } = useQuery({
    queryKey: [RequestKey.GetExhibitorPair, showId, exhibitorPairId],
    queryFn: async ({
      queryKey: [, showId, exhibitorPairId],
    }): Promise<IExhibitorPair | null> => {
      const isReady = showId && exhibitorPairId;
      if (!isReady) {
        return null;
      }

      try {
        const response = await apiV2.exhibitor.pair.getOne({
          showId,
          exhibitorPairId,
        });

        if (!response) {
          history(generatePath(SHOW_EXHIBITORS_PATH, { showId }));
        }

        return response;
      } catch {
        history(generatePath(SHOW_EXHIBITORS_PATH, { showId }));
        return null;
      }
    },
    enabled: !!exhibitorPairId,
    initialData: null,
  });

  useEffect(() => {
    apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
      key: NotificationType.UPDATED_BUILD_TYPE,
      showId,
      listenerId: "UpdateBuildTypeFromUseExhibitorPair",
      callback: async ({ showId }) => {
        await queryClient.invalidateQueries({
          queryKey: [RequestKey.GetExhibitorPair, showId, exhibitorPairId],
        });
      },
    });

    apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
      key: NotificationType.CREATED_CUSTOM_FIELD,
      showId,
      listenerId: "CREATED_CUSTOM_FIELD_FROM_USE_EXHIBITOR_PAIR",
      callback: async ({ showId }) => {
        await queryClient.invalidateQueries({
          queryKey: [RequestKey.GetExhibitorPair, showId, exhibitorPairId],
        });
      },
    });

    apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
      key: NotificationType.UPDATED_CUSTOM_FIELD,
      showId,
      listenerId: "UPDATED_CUSTOM_FIELD_FROM_USE_EXHIBITOR_PAIR",
      callback: async ({ showId }) => {
        await queryClient.invalidateQueries({
          queryKey: [RequestKey.GetExhibitorPair, showId, exhibitorPairId],
        });
      },
    });

    apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
      key: NotificationType.DELETED_CUSTOM_FIELD,
      showId,
      listenerId: "DELETED_CUSTOM_FIELD_FROM_USE_EXHIBITOR_PAIR",
      callback: async ({ showId }) => {
        await queryClient.invalidateQueries({
          queryKey: [RequestKey.GetExhibitorPair, showId, exhibitorPairId],
        });
      },
    });

    apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
      key: NotificationType.UPDATED_STAND,
      showId,
      listenerId: "UPDATED_STAND_FROM_USE_EXHIBITOR_PAIR",
      callback: async ({ showId }) => {
        await queryClient.invalidateQueries({
          queryKey: [RequestKey.GetExhibitorPair, showId, exhibitorPairId],
        });
      },
    });

    apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
      key: NotificationType.BULK_UPDATED_STANDS,
      showId,
      listenerId: "BULK_UPDATED_STANDS_FROM_USE_EXHIBITOR_PAIR",
      callback: async ({ showId }) => {
        await queryClient.invalidateQueries({
          queryKey: [RequestKey.GetExhibitorPair, showId, exhibitorPairId],
        });
      },
    });

    apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
      key: NotificationType.UPDATED_CUSTOM_FIELD_TO_STAND,
      listenerId: "UPDATED_CUSTOM_FIELD_TO_STAND_FROM_USE_EXHIBITOR_PAIR",
      showId,
      callback: async ({ showId }) => {
        await queryClient.invalidateQueries({
          queryKey: [RequestKey.GetExhibitorPair, showId, exhibitorPairId],
        });
      },
    });

    apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
      key: NotificationType.UPDATED_RELATION_BETWEEN_STAND_AND_PACKAGE,
      showId,
      listenerId:
        "UPDATED_RELATION_BETWEEN_STAND_AND_PACKAGE_FROM_USE_EXHIBITOR_PAIR",
      callback: async ({ showId }) => {
        await queryClient.invalidateQueries({
          queryKey: [RequestKey.GetExhibitorPair, showId, exhibitorPairId],
        });
      },
    });

    apiV2.notification.subscribe<
      INotificationBaseRelatedToShow<IPayloadUpdatedExhibitorPair>
    >({
      key: NotificationType.UPDATED_EXHIBITOR_PAIR,
      showId,
      listenerId: "UPDATED_EXHIBITOR_PAIR_FROM_USE_EXHIBITOR_PAIR",
      callback: async ({ showId, payload }) => {
        if (!payload) {
          return;
        }

        if (payload.exhibitorPairId !== exhibitorPairId) {
          return;
        }

        await queryClient.invalidateQueries({
          queryKey: [RequestKey.GetExhibitorPair, showId, exhibitorPairId],
        });
      },
    });

    return () => {
      apiV2.notification.unsubscribe({
        key: NotificationType.UPDATED_BUILD_TYPE,
        showId,
        listenerId: "UpdateBuildTypeFromUseExhibitorPair",
      });

      apiV2.notification.unsubscribe({
        key: NotificationType.CREATED_CUSTOM_FIELD,
        showId,
        listenerId: "CREATED_CUSTOM_FIELD_FROM_USE_EXHIBITOR_PAIR",
      });

      apiV2.notification.unsubscribe({
        key: NotificationType.BULK_UPDATED_STANDS,
        showId,
        listenerId: "BULK_UPDATED_STANDS_FROM_USE_EXHIBITOR_PAIR",
      });

      apiV2.notification.unsubscribe({
        key: NotificationType.UPDATED_CUSTOM_FIELD,
        showId,
        listenerId: "UPDATED_CUSTOM_FIELD_FROM_USE_EXHIBITOR_PAIR",
      });

      apiV2.notification.unsubscribe({
        key: NotificationType.DELETED_CUSTOM_FIELD,
        showId,
        listenerId: "DELETED_CUSTOM_FIELD_FROM_USE_EXHIBITOR_PAIR",
      });

      apiV2.notification.unsubscribe({
        key: NotificationType.UPDATED_STAND,
        showId,
        listenerId: "UPDATED_STAND_FROM_USE_EXHIBITOR_PAIR",
      });

      apiV2.notification.unsubscribe({
        key: NotificationType.UPDATED_CUSTOM_FIELD_TO_STAND,
        listenerId: "UPDATED_CUSTOM_FIELD_TO_STAND_FROM_USE_EXHIBITOR_PAIR",
        showId,
      });

      apiV2.notification.unsubscribe({
        key: NotificationType.UPDATED_RELATION_BETWEEN_STAND_AND_PACKAGE,
        showId,
        listenerId:
          "UPDATED_RELATION_BETWEEN_STAND_AND_PACKAGE_FROM_USE_EXHIBITOR_PAIR",
      });

      apiV2.notification.unsubscribe({
        key: NotificationType.UPDATED_EXHIBITOR_PAIR,
        showId,
        listenerId: "UPDATED_EXHIBITOR_PAIR_FROM_USE_EXHIBITOR_PAIR",
      });
    };
  }, [exhibitorPairId]);

  return [exhibitorPair, isLoading];
}

export default useExhibitorPair;
